import { computed, effect, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { catchError, EMPTY, Observable, take, tap } from 'rxjs';
import {
  ApiService,
  GenericResponseCode,
  GenericResponseResult,
} from '@brighthub/api';

import {
  ClientSubjectData,
  UiSession,
  UiSessionResponse,
  UiSessionsResponse,
} from './ui-session.model';

import { CopilotFacadeService } from '@brighthub/copilot';
import { MessageService } from 'primeng/api';
import { AmplitudeService } from '@brighthub/logging';
import { AuthService, OktaUserCacheService } from '@brightside/core/auth';


@Injectable({
  providedIn: 'root',
})
export class ClientSessionService implements OnDestroy {
  private _sessions = signal<UiSession[]>([]);
  readonly sessions = computed(() =>
    [...this._sessions()].sort((a, b) =>
      new Date(b[this._sortBy()]).getTime() -
      new Date(a[this._sortBy()]).getTime()
    )
  );
  private _archivedSessions = signal<UiSession[]>([]);
  readonly archivedSessions = computed(() =>
    [...this._archivedSessions()].sort((a, b) =>
      new Date(b[this._sortBy()]).getTime() -
      new Date(a[this._sortBy()]).getTime()
    )
  );
  private _loading = signal(true);
  private _sortBy = signal<'createdDate'>('createdDate');
  private _api = inject(ApiService);
  private _facade = inject(CopilotFacadeService);
  private readonly _messageService = inject(MessageService);
  private _amplitude = inject(AmplitudeService);
  private _selectedSubjectData = signal<ClientSubjectData | null>(null);
  readonly selectedSubjectData = this._selectedSubjectData.asReadonly();
  private _selectedSession = signal<UiSession | null>(null);
  readonly selectedSession = this._selectedSession.asReadonly();

  private _showArchived = signal(false);
  readonly showArchived = this._showArchived.asReadonly();
  private _selectedConversationId = signal<string | null>(null);
  readonly selectedConversationId = this._selectedConversationId.asReadonly();

  private _lastActiveSession = signal<UiSession | null>(null);
  private _lastArchivedSession = signal<UiSession | null>(null);
  private _initialized = signal(false);
  readonly initialized = this._initialized.asReadonly();

  readonly auth = inject(AuthService);
  readonly loggedInUserId = signal('');

  private _oktaUserCache = inject(OktaUserCacheService);
  private _channel = new BroadcastChannel('brightai-sessions');

  get isReady() {
    return this.initialized;
  }

  toggleArchivedView() {
    this._showArchived.set(!this._showArchived());
  }

  setArchivedView(value: boolean) {
    this._showArchived.set(value);
  }


  constructor() {
    effect(() => {
      const _ = this._facade.clearContextSignal();
      // this._selectedSession.set(null);
      this.setSelectedSubject(null);
      // this._selectedConversationId.set(null);
    });

    this._channel.onmessage = (event) => {
      const { type, payload } = event.data ?? {};
      if (!type || !payload) return;

      switch (type) {
        case 'setSessions':
          this._sessions.set(payload);

          break;
        case 'setArchivedSessions':
          this._archivedSessions.set(payload);
          break;
      }
    };


    effect(() => {
      const sessions = this._sessions();
      const archived = this._archivedSessions();
      const selected = this._selectedSession();

      if (
        selected &&
        !sessions.some(s => s.uiSessionId === selected.uiSessionId) &&
        !archived.some(s => s.uiSessionId === selected.uiSessionId)
      ) {
        this._facade.clearContext();
      }
    });

    this.auth.getUser().then((user) => {
      if (user) {
        this.loggedInUserId.set(user.sub);
      }
    });

  }

  setSelectedConversation(id: string | null) {
    this._selectedConversationId.set(id);
  }

  rememberLastSession(session: UiSession) {
    if (session.state === 'archived') {
      this._lastArchivedSession.set(session);
    } else {
      this._lastActiveSession.set(session);
    }
  }

  restorePreviousSessionOrClear(view: 'active' | 'archived') {
    const session =
      view === 'archived' ? this._lastArchivedSession() : this._lastActiveSession();

    if (session) {
      this.createOrSelectClientConversation(session);
    } else {
      if (!this.ensureOneClientSelected()) {
        this._facade.clearContext();
      }
    }
  }

  findActiveSessionForSubject(subjectId: string): UiSession | null {
    return this._sessions().find(
      (s) => s.subjectId === subjectId && s.state === 'active'
    ) ?? null;
  }

  setSelectedSubject(subjectData: ClientSubjectData|null) {
    this._selectedSubjectData.set(subjectData);
  }

  /** Set the currently selected session (e.g. when user clicks a client in the list). */
  setSelectedSession(session: UiSession | null): void {
    this._selectedSession.set(session);
  }

  /**
   * Ensure one client is selected when the current view has sessions.
   * Used so the My Clients page never shows "no selection" when the list is non-empty.
   * @returns true if a session is selected (or was already selected in the current list), false if the list is empty
   */
  ensureOneClientSelected(): boolean {
    const list = this._showArchived() ? this._archivedSessions() : this._sessions();
    if (list.length === 0) return false;
    const current = this._selectedSession();
    if (current && list.some((s) => s.uiSessionId === current.uiSessionId)) return true;
    this._selectedSession.set(list[0]);
    return true;
  }

  get loading() {
    return this._loading.asReadonly();
  }

  setSortBy(field: 'createdDate') {
    this._sortBy.set(field);
  }

  loadClientSessions(): void {
    this._loading.set(true);

    this.getUiSession('?subjectType=client')
      .subscribe({
        next: (res) => {
          const activeSessions = (res.payload.uiSessions ?? []).filter((s: UiSession) => {
            return s.state === 'active' && this.hasSubjectData(s.subjectData);
          });
          const archivedSessions = (res.payload.uiSessions ?? []).filter((s: UiSession) => {
            return s.state === 'archived' && this.hasSubjectData(s.subjectData)
          });
          const merged = this._mergeSessions(this._sessions(), activeSessions);
          const archivedMerged = this._mergeSessions(
            this._archivedSessions(),
            archivedSessions
          );
          this._sessions.set(merged);
          this._archivedSessions.set(archivedMerged);
          this._loading.set(false);
          this._initialized.set(true);
          // Pre-select first client so the page never loads with "no selection" when list is non-empty
          if (merged.length > 0 && this._selectedSession() === null) {
            this._selectedSession.set(merged[0]);
          }
        },
        error: (err) => {
          this._loading.set(false);
          this._initialized.set(true);
          this._messageService.add({
            severity: 'error',
            summary: 'Failed to get UI Sessions',
            detail: err.message,
          });
        },
      });
  }

  createClientSession(subjectId: string) {
    const body = {
      subjectType: 'client',
      subjectId,
    };

    return this._api
      .put<UiSessionResponse>('api-mobile', '/ui-session', body)
      .pipe(
        tap((sessionResponse) => {
          const session = sessionResponse.payload;
          this.addOrUpdateSession(session);
          if (session.conversationId) {
            const guid = session.subjectData.guid;
            this._facade.sendMessage(
              session.subjectId,
              session.conversationId,
              guid,
              undefined,
              this.loggedInUserId()
            );
            this._amplitude.logEvent('create_conversation', {
              conversationId: session.conversationId,
              uiSessionId: session.uiSessionId,
              subjectGuid: session.subjectData.guid,
              subjectType: 'client',
            });
          }
        }),
        catchError((err) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Failed to create UI session',
            detail: err.message,
          });
          this._amplitude.logEvent('create_conversation_error', {
            message: err.message,
            subjectGuid: subjectId,
            subjectType: 'client',
            status: err.status
          });
          return EMPTY;
        })
      );
  }

  addOrUpdateSession(session: UiSession): void {
    const merged = this._mergeSessions(this._sessions(), [session]);
    this._sessions.set(merged);
    this._broadcastSessions();
  }

  private _broadcastSessions() {
    this._channel.postMessage({ type: 'setSessions', payload: this._sessions() });
    this._channel.postMessage({ type: 'setArchivedSessions', payload: this._archivedSessions() });
  }

  private _mergeSessions(existing: UiSession[], incoming: UiSession[]): UiSession[] {
    const map = new Map(existing.map((s) => [s.uiSessionId, s]));
    for (const session of incoming) {
      map.set(session.uiSessionId, session);
    }
    return Array.from(map.values());
  }
  archiveClientSession(uiSessionId: string) {
    const sessionToDelete = this._sessions().find((s) => s.uiSessionId === uiSessionId);

    this._facade.selectedConversation$.pipe(
      take(1),
      tap((conversationId) => {
        this._api.del<void>('api-mobile', `/ui-session/${uiSessionId}`).subscribe({
          next: () => {
            const archivedSession = this._sessions().filter((s) => s.uiSessionId === uiSessionId);
            const updated = this._sessions().filter((s) => s.uiSessionId !== uiSessionId);

            this._sessions.set(updated);
            this._archivedSessions.set(this._mergeSessions(archivedSession, this._archivedSessions()));

            if (sessionToDelete?.subjectId) {
              this._facade.clearClient(sessionToDelete.subjectId);
              if (this._selectedSubjectData()?.guid === sessionToDelete.subjectData.guid) {
                this._selectedSubjectData.set(null);
              }
            }

            if (conversationId && conversationId === sessionToDelete?.conversationId) {
              this._lastActiveSession.set(null);
              if (updated.length > 0) {
                this._selectedSession.set(updated[0]);
              } else {
                this._facade.clearContext();
              }
            }

            this._amplitude.logEvent('archive_conversation', {
              conversationId: sessionToDelete?.conversationId,
              uiSessionId: sessionToDelete?.uiSessionId,
              subjectGuid: sessionToDelete?.subjectData.guid,
              subjectType: 'client',
            });

            this._broadcastSessions();
          },
          error: (err) => {
            this._messageService.add({
              severity: 'error',
              summary: 'Failed to delete UI Session',
              detail: err.message,
            });
            this._amplitude.logEvent('archive_conversation_error', {
              message: err.message,
              conversationId: sessionToDelete?.conversationId,
              uiSessionId: sessionToDelete?.uiSessionId,
              subjectGuid: sessionToDelete?.subjectData.guid,
              subjectType: 'client',
              status: err.status
            });
          },
        });
      })
    ).subscribe();
  }

  createOrSelectClientConversation(input: UiSession | string): void {
    if (typeof input === 'string') {
      // Treat input as GUID
      this.createClientSession(input).subscribe({
        next: (res) => {
          const session = res.payload;
          this._selectedSession.set(session);
          this.setSelectedSubject(session.subjectData);
        },
        error: (err) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Failed to create client session',
            detail: err.message,
          });
        },
      });

      return;
    }

    // If it's a UiSession object
    const session = input;

    if (session.conversationId) {
      this._selectedSession.set(session);
      this.setSelectedSubject(session.subjectData);
      this._facade.clearConversationHighlight(session.subjectData.guid, session.conversationId);

      this._amplitude.logEvent('select_existing_conversation', {
        conversationId: session.conversationId,
        uiSessionId: session.uiSessionId,
        subjectGuid: session.subjectData.guid,
        subjectType: 'client',
        state: session.state,
      });
      return;
    }

    this.createClientSession(session.subjectData.guid).subscribe({
      next: (res) => {
        const session = res.payload;
        this._selectedSession.set(session);
        this.setSelectedSubject(session.subjectData);
      },
      error: (err) => {
        this._messageService.add({
          severity: 'error',
          summary: 'Failed to create client session',
          detail: err.message,
        });
      },
    });
  }

  hasSubjectData(obj: unknown): obj is ClientSubjectData {
    return !!obj && typeof obj === 'object' && Object.keys(obj).length > 0;
  }

  getUiSession<T = UiSessionsResponse>(args?: string, body?: unknown): Observable<T & GenericResponseResult> {
    const path = `/ui-session${args ?? ''}`;
    return this._api.get<T>('api-mobile', path, body)
      .pipe(
        tap((resp:any) => {
          if (resp.result.code === GenericResponseCode.SERVICE_OK) {
            const sessions: UiSession[] = resp.payload.uiSessions || [resp.payload.uiSession];
            this._oktaUserCache.addUsersFromSessions(sessions);
          }
        })
      );
  }

  ngOnDestroy(): void {
    this._channel.close();
  }

}
